import { useSyncExternalStore } from "react";

export interface IValidateRule {
  isValid: (value: any) => boolean | Promise<boolean>;
  errorMessage: string;
}

export type IRules<F> = Record<keyof F, IValidateRule[]>;

export const createFormValidation = <S extends Record<string, any>>(
  initFields: S,
  rules: IRules<S>
) => {
  let fields: S = { ...initFields };
  let errors = Object.keys(fields).reduce<Record<keyof S, string>>(
    (prev, next: keyof S) => {
      prev[next] = "";
      return prev;
    },
    {} as any
  );

  const Validate = (fieldName: keyof S) => {
    const unvalidRule = rules[fieldName].find((rule) => {
      const isValid = rule.isValid(fields[fieldName]);
      if (isValid) {
        return false;
      } else {
        return true;
      }
    });

    if (unvalidRule) {
      if (errors[fieldName] != unvalidRule.errorMessage) {
        errors[fieldName] = unvalidRule.errorMessage;
        errorCallbacks.forEach(([fieldN, cb]) => {
          if (fieldN == fieldName) cb();
        });
      }
    } else {
      if (errors[fieldName] != "") {
        errors[fieldName] = "";
        errorCallbacks.forEach(([fieldN, cb]) => {
          if (fieldN == fieldName) cb();
        });
      }
    }

    return !!unvalidRule;
  };

  const isFieldsValid = () => {
    return Object.keys(fields)
      .map((field) => {
        return Validate(field);
      })
      .some((val) => !val);
  };

  const resetFormFields = () => {
    fields = { ...initFields };
    fieldCallbacks.forEach(([n, cb]) => cb());
    isFieldsValid();
  };

  const setField = <V extends keyof S>(fieldName: V, value: S[V]) => {
    fields[fieldName] = value;
    fieldCallbacks.forEach(([fieldN, cb]) => {
      if (fieldN == fieldName) cb();
    });
    Validate(fieldName);
  };

  const fieldCallbacks = new Set<[keyof S, () => void]>();
  const errorCallbacks = new Set<[keyof S, () => void]>();

  const subscribeField = (fieldName: keyof S) => (callback: () => void) => {
    fieldCallbacks.add([fieldName, callback]);
    return () => fieldCallbacks.delete([fieldName, callback]);
  };
  const subscribeError = (fieldName: keyof S) => (callback: () => void) => {
    errorCallbacks.add([fieldName, callback]);
    return () => errorCallbacks.delete([fieldName, callback]);
  };

  const useFieldValue = <F extends keyof S>(fieldName: F) => {
    return useSyncExternalStore(
      subscribeField(fieldName),
      () => fields[fieldName]
    );
  };
  const useFieldError = <F extends keyof S>(fieldName: F) => {
    return useSyncExternalStore(
      subscribeError(fieldName),
      () => errors[fieldName]
    );
  };

  return {
    useFieldValue,
    useFieldError,
    getFields: () => fields,
    setField,
    isFieldsValid,
    resetFormFields,
  };
};
